import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, IconButton, Typography, CircularProgress, Tooltip, } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Swal from 'sweetalert2';

export interface DocumentViewerItem {
    documentUrl: string;
    documentName: string;
};

export interface DocumentViewerProps {
    open: boolean;
    documents: DocumentViewerItem[];
    onClose: () => void;
};
export const DocumentViewer: React.FC<DocumentViewerProps> = ({ open, documents, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    
    const loadDocument = useCallback(async (document: DocumentViewerItem) => {
        if (!document.documentUrl) return;
        
        const extension = (document.documentName || document.documentUrl).split('.').pop()?.toLowerCase();
        const isPdf = extension === 'pdf';
        const isOfficeFile = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension || '');
        
        setLoading(true);
        setFileName(document.documentName);
        
        try {
            if (isPdf) {
                const response = await fetch(document.documentUrl, {
                    method: 'GET',
                    mode: 'cors',
                    credentials: 'omit',
                    cache: 'no-cache',
                });
                if (!response.ok) {
                    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
                }
                const downloadedBlob = await response.blob();
                const cleanBlob = new Blob([downloadedBlob], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(cleanBlob);
                setDocumentUrl(blobUrl);
            } else if (isOfficeFile) {
                const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(document.documentUrl)}`;
                setDocumentUrl(officeViewerUrl);
            } else {
                const response = await fetch(document.documentUrl, {
                    method: 'GET',
                    mode: 'cors',
                    credentials: 'omit',
                    cache: 'no-cache',
                });
                if (!response.ok) {
                    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
                }
                const downloadedBlob = await response.blob();
                const contentType = response.headers.get('content-type') || 'application/octet-stream';
                const cleanBlob = new Blob([downloadedBlob], { type: contentType });
                const blobUrl = URL.createObjectURL(cleanBlob);
                setDocumentUrl(blobUrl);
            }
        } catch (error) {
            console.error('Error loading document:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to load file. Opening in new tab instead.',
                icon: 'warning',
                confirmButtonText: 'OK',
            }).then(() => {
                window.open(document.documentUrl, '_blank');
            });
        } finally {
            setLoading(false);
        }
    }, []);
    
    const isInitialOpen = useRef(true);
    
    useEffect(() => {
        if (!open || !documents.length) return;
        setCurrentIndex(0);
        setDocumentUrl(null);
        loadDocument(documents[0]);
        isInitialOpen.current = true;
    }, [open, documents, loadDocument]);
    
    useEffect(() => {
        if (!open || !documents.length) return;
        if (isInitialOpen.current) {
            isInitialOpen.current = false;
            return;
        }
        const doc = documents[currentIndex];
        if (doc && currentIndex >= 0 && currentIndex < documents.length) {
            setDocumentUrl(null);
            loadDocument(doc);
        }
    }, [currentIndex, open, documents, loadDocument]);
    
    const handlePrev = () => {
        if (currentIndex <= 0) return;
        if (documentUrl && documentUrl.startsWith('blob:')) {
            URL.revokeObjectURL(documentUrl);
        }
        setDocumentUrl(null);
        setCurrentIndex((i) => i - 1);
    };
    
    const handleNext = () => {
        if (currentIndex >= documents.length - 1) return;
        if (documentUrl && documentUrl.startsWith('blob:')) {
            URL.revokeObjectURL(documentUrl);
        }
        setDocumentUrl(null);
        setCurrentIndex((i) => i + 1);
    };
    
    const handleClose = () => {
        if (documentUrl && documentUrl.startsWith('blob:')) {
            URL.revokeObjectURL(documentUrl);
        }
        setDocumentUrl(null);
        setFileName(null);
        setCurrentIndex(0);
        onClose();
    };
    
    const showNavigation = open && documents.length > 1;
    
    return (
        <Dialog open={open} onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                style: { maxHeight: '90vh' },
            }}
        >
            <DialogTitle className="card-title">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        {showNavigation && (<>
                            <Tooltip title="Previous document">
                                <IconButton onClick={handlePrev}
                                    disabled={currentIndex <= 0 || loading}
                                    size="small"
                                    sx={{ flexShrink: 0 }}
                                    aria-label="Previous document"
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="body2" sx={{ flexShrink: 0, color: 'text.secondary' }}>
                                {currentIndex + 1} of {documents.length}
                            </Typography>
                            <Tooltip title="Next document">
                                <IconButton onClick={handleNext}
                                    disabled={currentIndex >= documents.length - 1 || loading}
                                    size="small"
                                    sx={{ flexShrink: 0 }}
                                    aria-label="Next document"
                                >
                                    <NavigateNextIcon />
                                </IconButton>
                            </Tooltip>
                        </>)}
                        <Box sx={{
                            fontWeight: 'medium',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ml: showNavigation ? 1 : 0,
                        }}>
                            {fileName || 'Document Viewer'}
                        </Box>
                    </Box>
                    <Tooltip title="Close">
                        <IconButton onClick={handleClose}
                            sx={{
                                color: '#666',
                                flexShrink: 0,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    color: '#000',
                                },
                            }}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ padding: 0, height: '80vh', position: 'relative' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : documentUrl ? (
                    <Box sx={{ width: '100%', height: '100%' }}>
                        <iframe title="Document Viewer" src={documentUrl}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                        />
                    </Box>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};
export default DocumentViewer;